import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Trophy, X } from "lucide-react";
import { useState } from "react";
import { getMyBracket } from "@/lib/bracket.functions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";

/**
 * Lightweight, ambient CTA capsule.
 * Reuses the already-cached ["bracket","me"] query — no new polling,
 * no realtime, no engine. Renders only when there is at least one
 * scheduled match the user can still predict on.
 */
export function QuinielaOpenCTA({ inline = false }: { inline?: boolean }) {
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const q = useQuery({
    queryKey: ["bracket", "me"],
    queryFn: () => getMyBracket(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  // PR-DISCOVERY-01 · P0-3 — auto-dismiss removed. Capsule persists until
  // the user taps the CTA or the explicit close (×) control.
  if (dismissed || !isAuthenticated) return null;
  const matches = q.data?.matches ?? [];
  const hasOpen = matches.some(
    (m) => m.status === "scheduled" && new Date(m.kickoff_at).getTime() > Date.now(),
  );
  if (!hasOpen) return null;

  return (
    <div
      className={
        inline
          ? "inline-flex w-full items-center justify-between gap-1 rounded-2xl border border-amber-400/60 bg-gradient-to-r from-amber-500/95 to-amber-600/95 py-2 pl-3 pr-1 text-[11px] font-black tracking-wider text-background shadow-lg backdrop-blur-md"
          : "fixed left-1/2 z-20 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-gradient-to-r from-amber-500/95 to-amber-600/95 pl-3 pr-1 py-1 text-[11px] font-black tracking-wider text-background shadow-lg backdrop-blur-md"
      }
      style={{
        bottom: inline ? undefined : isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 140px)" : 96,
      }}
    >
      <Link
        to="/quinielas"
        onClick={() => void trackEvent("quiniela_open_cta_clicked", { surface: "ambient_capsule" })}
        className="inline-flex items-center gap-1.5 transition hover:scale-105 active:scale-95"
        aria-label="Reto disponible — ir a quinielas"
      >
        <Trophy className="h-3.5 w-3.5" />
        🏆 RETO DISPONIBLE
        <span className="ml-0.5 opacity-80">→</span>
      </Link>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          void trackEvent("quiniela_open_cta_dismissed", { surface: "ambient_capsule" });
        }}
        aria-label="Cerrar"
        className="ml-1 grid h-6 w-6 place-items-center rounded-full bg-background/20 transition hover:bg-background/40"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}